import React, { useState } from "react";
import {
  Grid,
  PaginationItem,
  Paper,
  Skeleton,
  ThemeProvider,
  Typography,
  createTheme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import planActiveImage from "../../assets/images/planActiveImage.svg";
import planExpiredImage from "../../assets/images/planStatus.svg";
import { DataGrid } from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import SilverPlan from "../../assets/images/Silver.svg";
import GoldPlan from "../../assets/images/GoldImage.svg";
import PlatinumPlan from "../../assets/images/PlatinumImage.svg";
import TrialPlan from "../../assets/images/Trial.svg";
import { StyledPaginationItem } from "../../StyledComponents/StyledComponents";

const formatDateValue = (cellOrParams) => {
  if (!cellOrParams || cellOrParams === "-") return "-";
  let value = cellOrParams;
  if (typeof cellOrParams === "object" && !(cellOrParams instanceof Date)) {
    value = cellOrParams.value ?? cellOrParams.formattedValue ?? "-";
    if (typeof value === "object") return "-";
  }
  if (!value || value === "-") return "-";
  let d = new Date(value);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  const parts = String(value).split(/[-/]/);
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // 0-indexed
    const year = parseInt(parts[2], 10);
    d = new Date(year, month, day);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }
  return typeof value === "string" ? value : "-";
};

const safeRenderValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "object") {
    return "-";
  }
  return String(value);
};

export default function UsersTable(props) {
  const {
    planCount,
    resultLimit,
    planData,
    page,
    handlePageChange,
    isLoading,
    setPage,
    onRowClick,
  } = props;
  const endPageCount = page + 1;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const customLocaleText = {
    noResultsOverlayLabel: "No Users Found",
  };

  const columns = [
    {
      field: "Name",
      headerName: "Name",
      headerClassName: "super-app-theme--header",
      minWidth: 170,
      renderCell: (params) => <span style={{ fontWeight: "bold", color: "#2B2A35" }}>{safeRenderValue(params.row?.Name)}</span>,
    },
    {
      field: "DateOfBirth",
      headerName: "DOB",
      headerClassName: "super-app-theme--header",
      minWidth: 100,
      renderCell: (params) => <span>{formatDateValue(params.row?.DateOfBirth)}</span>,
    },
    {
      field: "RegisteredDate",
      headerName: "Created Date",
      headerClassName: "super-app-theme--header",
      minWidth: 120,
      renderCell: (params) => <span>{formatDateValue(params.row?.RegisteredDate || params.row?.todayDate || params.row?.createdAt)}</span>,
    },
    {
      field: "Mobile",
      headerName: "Mobile No.",
      headerClassName: "super-app-theme--header",
      minWidth: 150,
      renderCell: (params) => <span>{safeRenderValue(params.row?.Mobile)}</span>,
    },
    {
      field: "Email",
      headerName: "Mail Id",
      headerClassName: "super-app-theme--header",
      minWidth: 230,
      renderCell: (params) => <span>{safeRenderValue(params.row?.Email)}</span>,
    },
    {
      field: "Address",
      headerName: "Address",
      headerClassName: "super-app-theme--header",
      minWidth: 180,
      renderCell: (params) => <span>{safeRenderValue(params.row?.Address)}</span>,
    },
    {
      field: "CompanyName",
      headerName: "Insurance Companies",
      headerClassName: "super-app-theme--header",
      minWidth: 180,
      renderCell: (params) => <span>{safeRenderValue(params.row?.CompanyName)}</span>,
    },
    {
      field: "LastPlanStatus",
      headerName: "Plan Status",
      headerClassName: "super-app-theme--header",
      minWidth: 160,
      renderCell: (params) => {
        const status = String(params.row?.LastPlanStatus || "").trim().toLowerCase();
        if (status === "active") {
          return <img src={planActiveImage} alt="Active" />;
        } else if (status === "upcoming") {
          return (
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 65,
              height: 25,
              borderRadius: 6,
              backgroundColor: "rgba(74, 144, 226, 0.1)",
              border: "1px solid rgba(74, 144, 226, 0.2)",
              color: "#4A90E2",
              fontSize: "10px",
              fontWeight: "bold",
              fontFamily: "Open Sans, sans-serif"
            }}>
              UPCOMING
            </span>
          );
        } else {
          return <img src={planExpiredImage} alt="Expired" />;
        }
      },
    },
    {
      field: "LastPlanName",
      headerName: "Latest Plan",
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        let imageSource;
        const planName = params.row?.LastPlanName;
        switch (planName) {
          case "Silver":
            imageSource = SilverPlan;
            break;
          case "Gold":
            imageSource = GoldPlan;
            break;
          case "Platinum":
            imageSource = PlatinumPlan;
            break;
          case "nill":
          case "Trial":
            imageSource = TrialPlan;
            break;
          default:
            imageSource = null;
            break;
        }
        if (imageSource !== null) {
          return <img src={imageSource} alt="Image" />;
        } else {
          return "-";
        }
      },
      minWidth: 160,
    },
  ];

  const isPlanDataNotEmpty = Array.isArray(planData) ? planData : [];

  function CustomFooter() {
    const [selectedPage, setSelectedPage] = useState(1);
    const handlePage = (event, value) => {
      setSelectedPage(value);
      setPage(value - 1);
    };

    const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
    return (
      <Grid
        container
        style={{
          justifyContent: "space-between",
          paddingTop: "15px",
          paddingBottom: "15px",
          paddingLeft: isSmallerScreen ? "0px" : "15px",
        }}
      >
        <Grid item>
          {isSmallerScreen ? null : (
            <Typography
              sx={{
                color: "#9A9A9A",
                fontFamily: "Open Sans",
                fontSize: "14px",
                lineHeight: "30px",
              }}
            >
              Showing {resultLimit * page + 1} to {resultLimit * endPageCount}{" "}
              of {planCount}
            </Typography>
          )}
        </Grid>
        <Grid item>
          <Pagination
            count={Math.ceil(planCount / resultLimit)}
            shape="rounded"
            color="primary"
            page={page + 1}
            onChange={handlePage}
            boundaryCount={1}
            siblingCount={isMobile ? -1 : 1}
            renderItem={(item) => (
              <StyledPaginationItem
                item={item}
                {...item}
                sx={{
                  backgroundColor: item.selected ? "red" : "white",
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    );
  }

  if (isLoading == true) {
    return (
      <Skeleton
        sx={{ width: "100%", minHeight: "65vh" }}
        animation="wave"
        variant="rectangular"
      ></Skeleton>
    );
  }

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        "& .super-app-theme--header": {
          backgroundColor: "#FCF8FF",
          color: "#83868B",
          fontFamily: "Open Sans",
        },
      }}
    >
      <div style={{ height: "65vh", width: "100%" }}>
        <DataGrid
          rows={isPlanDataNotEmpty}
          getRowId={(row) => row.id || row._id || Math.random()}
          columns={columns}
          pagination
          paginationMode="server"
          pageSize={resultLimit}
          onPageChange={handlePageChange}
          rowCount={planCount || 10}
          components={{
            Footer: CustomFooter,
          }}
          onRowClick={(params) => {
            if (onRowClick) {
              onRowClick(params.row);
            }
          }}
          sx={{
            color: "#A6A4A4",
            fontFamily: "Open Sans",
            "& .MuiDataGrid-row": {
              cursor: onRowClick ? "pointer" : "default",
            }
          }}
          localeText={customLocaleText}
        />
      </div>
    </Paper>
  );
}
